import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "../Input";
import { Select } from "../Select";
import { useLocalizedStrings } from "../LocalizationProvider";
export const isValidDate = (dateString) => {
    if (!dateString?.trim())
        return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
};
export const isValidNumber = (numberString) => {
    if (!numberString?.trim())
        return false;
    const number = Number(numberString);
    return !isNaN(number) && isFinite(number);
};
export const normalizeNumericString = (numberString) => {
    if (!isValidNumber(numberString)) {
        return numberString;
    }
    const number = Number(numberString);
    return number.toString();
};
export const FilterValueInput = ({ fieldType, value, onChange, onDebouncedChange, onValidChange, placeholder, className, debounceDelay = 300 }) => {
    const L = useLocalizedStrings();
    const [localValue, setLocalValue] = useState(value);
    const debounceTimeoutRef = useRef(null);
    useEffect(() => {
        setLocalValue(value);
    }, [value]);
    const handleImmediateChange = useCallback((newValue) => {
        setLocalValue(newValue);
        onChange(newValue);
    }, [onChange]);
    const handleDebouncedChange = useCallback((newValue) => {
        setLocalValue(newValue);
        onChange(newValue);
        if (onDebouncedChange) {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            debounceTimeoutRef.current = setTimeout(() => {
                onDebouncedChange(newValue);
            }, debounceDelay);
        }
    }, [onChange, onDebouncedChange, debounceDelay]);
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);
    switch (fieldType) {
        case 'number':
            return (_jsx(Input, { type: "number", thousandSeparator: ",", allowNegative: true, value: localValue, onChange: (e) => {
                    const inputValue = e.target.value;
                    setLocalValue(inputValue);
                    onChange(inputValue);
                    if (isValidNumber(inputValue)) {
                        onValidChange?.(inputValue);
                    }
                }, onBlur: (e) => {
                    const inputValue = e.target.value;
                    if (isValidNumber(inputValue)) {
                        const normalizedValue = normalizeNumericString(inputValue);
                        setLocalValue(normalizedValue);
                        onChange(normalizedValue);
                        onValidChange?.(normalizedValue);
                    }
                }, placeholder: placeholder || L.component_library.filter_panel.enter_a_value, label: L.component_library.filter_panel.filter_value, showLabel: false, className: className }));
        case 'date':
            const isDateValid = localValue === '' || isValidDate(localValue);
            return (_jsx(Input, { type: "date", dateFormat: "MM/DD/YYYY", value: localValue, onChange: (e) => {
                    const newValue = e.target.value;
                    setLocalValue(newValue);
                    onChange(newValue);
                    if (newValue !== '' && isValidDate(newValue)) {
                        onValidChange?.(newValue);
                    }
                    else if (newValue === '') {
                        onValidChange?.(newValue);
                    }
                }, placeholder: "MM/DD/YYYY", label: L.component_library.filter_panel.filter_value, showLabel: false, className: className, error: !isDateValid ? {
                    hasError: !isDateValid,
                    errorMessage: L.component_library.filter_panel.enter_a_valid_date
                } : undefined }));
        case 'boolean':
            const booleanOptions = [
                { value: 'true', label: L.component_library.filter_panel.true },
                { value: 'false', label: L.component_library.filter_panel.false }
            ];
            const selectedBooleanOption = booleanOptions.find(opt => opt.value === localValue);
            return (_jsx(Select, { options: booleanOptions, value: selectedBooleanOption, onChange: (option) => handleImmediateChange(option.value), description: "", label: L.component_library.filter_panel.filter_value, labelLocation: "above", hideLabel: true, portal: true, className: className }));
        case 'text':
        default:
            return (_jsx(Input, { type: "text", value: localValue, onChange: (e) => {
                    handleDebouncedChange(e.target.value);
                }, placeholder: placeholder, label: L.component_library.filter_panel.filter_value, showLabel: false, className: className }));
    }
};
